import React from 'react';
import styled from 'styled-components';
import {
  ResponsiveValue,
  space,
  SpaceProps,
  position,
  PositionProps,
  zIndex,
  ZIndexProps,
  top,
  TopProps,
  left,
  LeftProps,
  bottom,
  BottomProps,
  right,
  RightProps,
  flex,
  FlexProps,
  alignSelf,
  AlignSelfProps,
  order,
  OrderProps,
  overflow,
  OverflowProps,
  display,
  DisplayProps,
  width,
  WidthProps,
  minWidth,
  MinWidthProps,
  maxWidth,
  MaxWidthProps,
  height,
  HeightProps,
  minHeight,
  MinHeightProps,
  maxHeight,
  MaxHeightProps,
  background,
  BackgroundProps,
  borders,
  BordersProps,
  borderRadius,
  BorderRadiusProps,
  boxShadow,
  color,
  colorStyle,
  fontSize,
  FontSizeProps
} from 'styled-system';
import { gaps, overflowX, overflowY } from 'src/helpers';
import { basicTheme } from 'src/theme';

export type BaseProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement> &
    React.LabelHTMLAttributes<HTMLLabelElement> &
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'width' | 'height'> &
    Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'size' | 'width' | 'height'> & {
      onClick?: any;
    },
  'color'
>;

export type BaseBoxProps = StyledSystemProps<
  'div',
  BaseProps &
    SpaceProps &
    FlexProps &
    AlignSelfProps &
    OrderProps & {
      color?: string;
      bg?: string;
      colors?: keyof ReturnType<typeof basicTheme.getCommonColorStyles>;
    }
>;
export const baseBoxStyles = [space, flex, alignSelf, order, colorStyle, color, props => props.css];
export const BaseBox = styled.div<BaseBoxProps>`
  ${baseBoxStyles};
`;

export type LayoutBoxProps = BaseBoxProps &
  PositionProps &
  ZIndexProps &
  OverflowProps &
  DisplayProps &
  WidthProps &
  MinWidthProps &
  MaxWidthProps &
  HeightProps &
  MinHeightProps &
  MaxHeightProps & {
    overflowX?: string;
    overflowY?: string;
    rowGap?: any;
    columnGap?: any;
  };
export const layoutBoxStyles = [
  ...baseBoxStyles,
  position,
  zIndex,
  gaps,
  overflow,
  overflowX,
  overflowY,
  display,
  width,
  maxWidth,
  minWidth,
  height,
  maxHeight,
  minHeight
];
export const LayoutBox = styled.div<LayoutBoxProps>`
  ${layoutBoxStyles}
`;
export type BoxProps = LayoutBoxProps &
  BackgroundProps &
  BorderRadiusProps &
  FontSizeProps & {
    boxShadow?: ResponsiveValue<keyof typeof basicTheme.shadows>;
  };
export const boxStyles = [...layoutBoxStyles, background, borderRadius, boxShadow, fontSize];
export const Box = styled.div<BoxProps>`
  ${boxStyles};
`;

export type BorderBoxProps = BoxProps & BordersProps;
export const BorderBox = styled(Box)<BorderBoxProps>`
  ${borders};
`;

export const positionStyles = [position, top, right, bottom, left, zIndex];
export type PositionBoxProps = BoxProps &
  TopProps &
  RightProps &
  BottomProps &
  LeftProps & {
    fullParentSize?: boolean;
  };
export const PositionBox = styled(Box)<PositionBoxProps>`
  ${top};
  ${right};
  ${left};
  ${bottom};
  ${props =>
    props.fullParentSize &&
    `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
  `};
`;

export const AbsoluteBox = styled(PositionBox)`
  position: absolute;
  & > .react-reveal {
    height: 100%;
  }
`;

export const AbsoluteCenterBox = styled(AbsoluteBox)`
  transform: translate(-50%, -50%);
`;
AbsoluteCenterBox.defaultProps = {
  top: '50%',
  left: '50%'
};

export const AbsoluteBgBox = styled(AbsoluteBox).attrs(props => ({
  as: 'img'
}))`
  pointer-events: none;
`;
